import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Basic client-side format check; backend re-validates as EmailStr.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * NewsletterForm — shared between the slim near-footer section and the footer column.
 * @param {"section"|"footer"} source  Where the signup happened (stored server-side).
 * @param {"stacked"|"inline"} layout  Visual layout; "inline" is used in the footer column.
 * @param {string} testidPrefix        Prefix for data-testids so both instances are distinct.
 */
export const NewsletterForm = ({ source = "section", layout = "stacked", testidPrefix = "newsletter" }) => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: value, consent, source });
      if (res?.data?.already_subscribed) {
        toast.success("You're already subscribed — thank you.");
      } else {
        toast.success("Check your inbox to confirm your subscription.");
      }
      setDone(true);
      setEmail("");
      setConsent(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many attempts. Please try again shortly.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isInline = layout === "inline";
  const inputStyle = {
    backgroundColor: "#0E0A16",
    border: "1px solid #2A2233",
    color: "#F4EEF7",
  };

  if (done) {
    return (
      <div
        data-testid={`${testidPrefix}-success`}
        className="text-sm leading-relaxed"
        style={{ color: "#9B8FAC" }}
      >
        Check your inbox for a one-click confirmation from{" "}
        <span style={{ color: "#F4EEF7" }}>hello@vynlworld.com</span>. We'll only email you after you confirm.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`${testidPrefix}-form`}
      noValidate
      className={isInline ? "space-y-3" : "space-y-4"}
    >
      <div className={isInline ? "flex flex-col sm:flex-row gap-2" : "flex flex-col md:flex-row gap-3"}>
        <label htmlFor={`${testidPrefix}-email`} className="sr-only">Email address</label>
        <input
          id={`${testidPrefix}-email`}
          name="email"
          data-testid={`${testidPrefix}-email`}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@studio.com"
          className="flex-1 min-w-0 px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1]"
          style={inputStyle}
        />
        <button
          data-testid={`${testidPrefix}-submit`}
          type="submit"
          disabled={submitting}
          className="btn-sharp text-xs uppercase tracking-[0.22em] px-6 py-3 font-medium disabled:opacity-60 whitespace-nowrap"
          style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
        >
          {submitting ? "Sending…" : "Subscribe"}
        </button>
      </div>

      <label
        htmlFor={`${testidPrefix}-consent`}
        className="flex items-start gap-3 cursor-pointer"
        style={{ color: "#9B8FAC" }}
      >
        <input
          id={`${testidPrefix}-consent`}
          data-testid={`${testidPrefix}-consent`}
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-[3px] h-4 w-4 shrink-0 appearance-none border cursor-pointer transition-colors duration-200 checked:bg-[#C724B1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C724B1]"
          style={{ borderColor: "#2A2233", backgroundColor: consent ? "#C724B1" : "#0E0A16" }}
        />
        <span className="text-xs leading-relaxed">
          I agree to receive occasional emails from VYNLWORLD. I can unsubscribe at any time. See our{" "}
          <a
            href="/privacy"
            data-testid={`${testidPrefix}-privacy-link`}
            className="underline transition-colors duration-300 hover:text-[#C724B1]"
            style={{ color: "#F4EEF7" }}
          >
            Privacy Notice
          </a>
          .
        </span>
      </label>
    </form>
  );
};

export default NewsletterForm;
